import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import axios from 'axios';
import axiosInstance from '../../utils/axiosConfig';

interface ZatcaCertificate {
  _id: string;
  name: string;
  branchId: string;
  branchName?: string;
  environment: 'sandbox' | 'production';
  deviceId: string;
  status: 'active' | 'expired' | 'revoked' | 'inactive';
  issueDate?: Date;
  expiryDate: Date;
  serialNumber?: string;
  productionCertificate?: {
    secret?: string;
    binarySecurityToken?: string;
    requestId?: string;
    complianceRequestId?: string;
    tokenType?: string;
  };
  usageStats?: {
    totalInvoices: number;
    successfulSubmissions: number;
    failedSubmissions: number;
    lastUsedDate?: Date;
  };
}

interface Branch {
  _id: string;
  name: string;
}

const ZatcaCertificateSettings: React.FC = () => {
  const [certificates, setCertificates] = useState<ZatcaCertificate[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false); // ✅ نافذة عرض التفاصيل
  const [selectedCertificate, setSelectedCertificate] = useState<ZatcaCertificate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  const [certificateSaved, setCertificateSaved] = useState(false); // ✅ حالة جديدة لتتبع حفظ الشهادة
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [generatedData, setGeneratedData] = useState<{
    publicKey?: string;
    privateKey?: string;
    csr?: string;
    csrOriginal?: string;
    csrBase64?: string;
    csrPem?: string;
    csrId?: string;
    certificateId?: string;
    complianceCertificate?: any;
    complianceResult?: any;
    productionCertificate?: any;
  }>({});

  const steps = ['توليد المفاتيح والـ CSR', 'مرحلة الامتثال', 'الإرسال للهيئة', 'استلام الشهادة'];

  // حقول النموذج (للرفع اليدوي)
  const [formData, setFormData] = useState({
    name: '',
    branchId: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    deviceId: '',
    otp: '',
    certificateFile: null as File | null,
    privateKeyFile: null as File | null
  });

  // حقول نموذج التوليد التلقائي
  const [generateFormData, setGenerateFormData] = useState({
    name: '',
    branchId: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    deviceId: '',
    otp: '',
    // معلومات CSR حسب ZATCA
    commonName: '', // TST-886431145-399999999900003
    serialNumber: '', // 1-TST|2-TST|3-UUID
    organizationIdentifier: '', // 399999999900003 (15 رقم)
    organizationUnitName: '', // Riyadh Branch
    organizationName: '', // Maximum Speed Tech Supply LTD
    invoiceType: '1100', // 1100, 0100, 1000
    locationAddress: '', // RRRD2929
    industryBusinessCategory: '' // Supply activities
  });

  useEffect(() => {
    loadCertificates();
    loadBranches();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/zatca/certificates');
      setCertificates(response.data);
      setError(null);
    } catch (err: any) {
      setError('فشل تحميل الشهادات: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      // تأكد من أن البيانات array
      const branchesData = Array.isArray(response.data) ? response.data : [];
      setBranches(branchesData);
    } catch (err: any) {
      console.error('فشل تحميل الفروع:', err);
      setBranches([]); // تعيين array فارغ في حالة الخطأ
    }
  };

  const handleOpenDialog = (certificate?: ZatcaCertificate) => {
    // ✅ منع فتح نافذة التعديل للشهادات المولدة تلقائياً
    if (certificate && certificate.productionCertificate) {
      setError('لا يمكن تعديل الشهادات المولدة تلقائياً. يمكنك فقط عرض التفاصيل أو حذفها.');
      return;
    }
    
    if (certificate) {
      setSelectedCertificate(certificate);
      setFormData({
        name: certificate.name,
        branchId: certificate.branchId,
        environment: certificate.environment,
        deviceId: certificate.deviceId,
        otp: '',
        certificateFile: null,
        privateKeyFile: null
      });
    } else {
      setSelectedCertificate(null);
      setFormData({
        name: '',
        branchId: '',
        environment: 'sandbox',
        deviceId: '',
        otp: '',
        certificateFile: null,
        privateKeyFile: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCertificate(null);
    setError(null);
  };

  const handleFileChange = (field: 'certificateFile' | 'privateKeyFile') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // إنشاء FormData لرفع الملفات
      const data = new FormData();
      data.append('name', formData.name);
      
      // إضافة branchId فقط إذا تم اختياره
      if (formData.branchId) {
        data.append('branchId', formData.branchId);
      }
      
      data.append('environment', formData.environment);
      data.append('deviceId', formData.deviceId);
      
      if (formData.otp) {
        data.append('otp', formData.otp);
      }

      if (formData.certificateFile) {
        data.append('certificateFile', formData.certificateFile);
      }

      if (formData.privateKeyFile) {
        data.append('privateKeyFile', formData.privateKeyFile);
      }

      if (selectedCertificate) {
        // تحديث شهادة موجودة
        await axios.put(`/api/zatca/certificates/${selectedCertificate._id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSuccess('تم تحديث الشهادة بنجاح');
      } else {
        // إنشاء شهادة جديدة
        await axios.post('/api/zatca/certificates', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSuccess('تم إضافة الشهادة بنجاح');
      }

      handleCloseDialog();
      loadCertificates();
    } catch (err: any) {
      setError('فشل حفظ الشهادة: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الشهادة؟')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/api/zatca/certificates/${id}`);
      setSuccess('تم حذف الشهادة بنجاح');
      loadCertificates();
    } catch (err: any) {
      setError('فشل حذف الشهادة: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // المرحلة الأولى: توليد المفاتيح والـ CSR
  const handleStepOne = async () => {
    try {
      setGenerating(true);
      setError(null);

      // التحقق من الحقول المطلوبة
      if (!generateFormData.organizationName || !generateFormData.organizationIdentifier ||
          !generateFormData.organizationUnitName || !generateFormData.locationAddress || !generateFormData.deviceId) {
        setError('يرجى ملء جميع معلومات المنشأة المطلوبة');
        setGenerating(false);
        return;
      }

      setGenerationStep('جاري توليد المفاتيح والـ CSR...');

      // إرسال طلب لتوليد المفاتيح والـ CSR
      const response = await axiosInstance.post('/zatca/certificates/generate-csr', {
        commonName: generateFormData.commonName,
        serialNumber: generateFormData.serialNumber,
        organizationIdentifier: generateFormData.organizationIdentifier,
        organizationUnitName: generateFormData.organizationUnitName,
        organizationName: generateFormData.organizationName,
        invoiceType: generateFormData.invoiceType,
        locationAddress: generateFormData.locationAddress,
        industryBusinessCategory: generateFormData.industryBusinessCategory,
        deviceId: generateFormData.deviceId,
        environment: generateFormData.environment  // ✅ تمرير البيئة
      });

      // حفظ البيانات المولدة (PEM للتحميل، Original للإرسال)
      setGeneratedData({
        publicKey: response.data.publicKey,       // PEM format
        privateKey: response.data.privateKey,     // PEM format
        csr: response.data.csr,                   // PEM format (للتحميل)
        csrOriginal: response.data.csrOriginal,   // CSR الأصلي من SDK (للإرسال للهيئة)
        csrBase64: response.data.csrBase64,       // Base64 نقي
        csrPem: response.data.csr,
        csrId: response.data.csrId || `CSR-${Date.now()}`
      });

      setSuccess('✅ تم توليد المفاتيح والـ CSR بنجاح!');
      setGenerationStep('');
      setActiveStep(1); // الانتقال للمرحلة الثانية
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      setError('فشل توليد المفاتيح والـ CSR: ' + errorMessage);
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  // المرحلة الثانية: الحصول على شهادة الامتثال وإجراء الاختبارات
  const handleStepTwo = async () => {
    try {
      setGenerating(true);
      setError(null);

      // الخطوة 1: الحصول على شهادة الامتثال من الهيئة باستخدام CSR الموجود
      setGenerationStep('جاري الحصول على شهادة الامتثال من الهيئة...');
      
      // استخدام CSR الأصلي من SDK (كما هو)
      if (!generatedData.csrOriginal) {
        throw new Error('لم يتم العثور على CSR. يرجى إكمال المرحلة الأولى أولاً.');
      }
      
      console.log('📋 إرسال CSR الأصلي من SDK للهيئة (أول 100 حرف):', generatedData.csrOriginal.substring(0, 100));
      
      const complianceResponse = await axiosInstance.post('/zatca/certificates/request-compliance', {
        name: `${generateFormData.name}-compliance`,
        branchId: generateFormData.branchId,
        environment: generateFormData.environment,
        deviceId: generateFormData.deviceId,
        otp: generateFormData.otp,
        csr: generatedData.csrOriginal, // CSR الأصلي من SDK (Base64 مُشفر مرتين)
        organizationIdentifier: generateFormData.organizationIdentifier,
        organizationName: generateFormData.organizationName
      }, {
        timeout: 120000 // 120 ثانية (دقيقتان)
      });

      // حفظ جميع البيانات من الاستجابة (مع الحفاظ على CSR الأصلي)
      setGeneratedData(prev => ({
        ...prev,
        complianceCertificate: complianceResponse.data.complianceCertificate,
        complianceResult: complianceResponse.data.complianceTestResults,
        // نحتفظ بـ CSR الأصلي من المرحلة الأولى
        csr: prev.csr || complianceResponse.data.csrBase64,
        csrPem: prev.csrPem
      }));

      setSuccess('✅ تم الحصول على شهادة الامتثال واجتياز جميع الاختبارات بنجاح!');
      setGenerationStep('');
      // لا ننتقل تلقائياً - ننتظر المستخدم يضغط "التالي"
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      setError('فشل اختبار الامتثال: ' + errorMessage);
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  // المرحلة الثالثة: طلب Production CSID
  const handleStepThree = async () => {
    try {
      setGenerating(true);
      setError(null);

      setGenerationStep('جاري طلب Production CSID من الهيئة...');

      // ✅ التحقق من وجود CSR الأصلي
      if (!generatedData.csrOriginal) {
        throw new Error('لم يتم العثور على CSR. يرجى إعادة المرحلة الأولى.');
      }

      // طلب Production CSID باستخدام Compliance Request ID و CSR الأصلي
      const response = await axiosInstance.post('/zatca/certificates/request-production-csid', {
        complianceRequestId: generatedData.complianceCertificate?.requestId,
        environment: generateFormData.environment,
        csr: generatedData.csrOriginal  // ✅ إرسال CSR الأصلي من المرحلة الأولى
      });

      setGeneratedData(prev => ({
        ...prev,
        productionCertificate: response.data.data || response.data.certificate,
        certificateId: (response.data.data?.requestId || response.data.certificate?.requestId)
      }));

      setSuccess('✅ تم الحصول على Production CSID بنجاح!');
      setGenerationStep('');
      setActiveStep(3); // الانتقال للمرحلة الرابعة
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      setError('فشل طلب Production CSID: ' + errorMessage);
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  // المرحلة الرابعة: حفظ الشهادة النهائية
  const handleStepFour = async () => {
    // منع الضغط المتكرر - التحقق من أن العملية ليست قيد التنفيذ أو تم الحفظ بالفعل
    if (generating || certificateSaved) {
      console.log('⚠️ عملية الحفظ قيد التنفيذ بالفعل أو تم الحفظ مسبقاً، تجاهل الطلب المكرر');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      setGenerationStep('جاري حفظ الشهادة...');

      console.log('📋 البيانات المتاحة للحفظ:', {
        productionCertificate: generatedData.productionCertificate,
        publicKey: generatedData.publicKey ? 'موجود' : 'غير موجود',
        privateKey: generatedData.privateKey ? 'موجود' : 'غير موجود'
      });

      // حفظ الشهادة في قاعدة البيانات باستخدام endpoint خاص بالتوليد التلقائي
      await axiosInstance.post('/zatca/certificates/save-generated', {
        name: generateFormData.name,
        branchId: generateFormData.branchId,
        environment: generateFormData.environment,
        deviceId: generateFormData.deviceId,
        publicKey: generatedData.publicKey,
        privateKey: generatedData.privateKey,
        certificate: generatedData.productionCertificate?.binarySecurityToken || generatedData.productionCertificate?.certificateContent,
        binarySecurityToken: generatedData.productionCertificate?.binarySecurityToken,
        certificateId: generatedData.productionCertificate?.requestId,
        requestId: generatedData.productionCertificate?.requestId,
        secret: generatedData.productionCertificate?.secret,
        complianceRequestId: generatedData.complianceCertificate?.requestId,
        tokenType: generatedData.productionCertificate?.tokenType
      });

      setCertificateSaved(true); // ✅ تعيين الحالة لمنع الحفظ المتكرر
      setSuccess('✅ تم حفظ الشهادة بنجاح!');
      setGenerationStep('');
      
      // إعادة تعيين كل شيء
      setTimeout(() => {
        handleCloseGenerateDialog();
        loadCertificates();
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      setError('فشل حفظ الشهادة: ' + errorMessage);
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  // تحميل ملف
  const downloadFile = (content: string, filename: string, type: string = 'application/x-pem-file') => {
    // التحقق من نوع المحتوى: إذا كان يبدأ بـ ----- فهو PEM، وإلا فهو Base64
    let fileContent: string;
    
    console.log('📥 تحميل ملف:', filename);
    console.log('📋 المحتوى يبدأ بـ:', content.substring(0, 50));
    
    if (content.startsWith('-----BEGIN')) {
      // المحتوى بصيغة PEM، استخدمه مباشرة
      console.log('✅ المحتوى بصيغة PEM');
      fileContent = content;
    } else {
      // المحتوى بصيغة Base64، لا نفك التشفير - نستخدمه مباشرة
      // لأن المفاتيح مُخزنة بصيغة PEM وليست Base64 نقي
      console.log('⚠️ المحتوى ليس PEM، استخدامه مباشرة');
      fileContent = content;
    }
    
    const blob = new Blob([fileContent], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // وظيفة التوليد التلقائي للشهادة
  const handleGenerateCertificate = async () => {
    try {
      setGenerating(true);
      setError(null);

      // التحقق من الحقول المطلوبة
      if (!generateFormData.name || !generateFormData.deviceId || !generateFormData.otp) {
        setError('يرجى ملء جميع الحقول المطلوبة');
        return;
      }

      if (!generateFormData.organizationName || !generateFormData.organizationIdentifier ||
          !generateFormData.organizationUnitName || !generateFormData.locationAddress) {
        setError('يرجى ملء جميع معلومات المنشأة المطلوبة');
        return;
      }

      setGenerationStep('جاري توليد CSR والمفتاح الخاص...');

      // إرسال الطلب إلى Backend باستخدام axiosInstance الذي يضيف token تلقائياً
      const response = await axiosInstance.post('/zatca/certificates/generate', generateFormData);

      setSuccess('تم توليد الشهادة وطلبها من الهيئة بنجاح! ✅');
      setGenerationStep('');
      setOpenGenerateDialog(false);
      
      // إعادة تعيين النموذج
      setGenerateFormData({
        name: '',
        branchId: '',
        environment: 'sandbox',
        deviceId: '',
        otp: '',
        commonName: '',
        serialNumber: '',
        organizationIdentifier: '',
        organizationUnitName: '',
        organizationName: '',
        invoiceType: '1100',
        locationAddress: '',
        industryBusinessCategory: ''
      });

      // تحديث قائمة الشهادات
      loadCertificates();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      setError('فشل توليد الشهادة: ' + errorMessage);
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseGenerateDialog = () => {
    setOpenGenerateDialog(false);
    setGenerationStep('');
    setError(null);
    setSuccess(null);
    setActiveStep(0);
    setGeneratedData({});
    setCertificateSaved(false); // ✅ إعادة تعيين حالة الحفظ
    setGenerateFormData({
      name: '',
      branchId: '',
      environment: 'sandbox',
      deviceId: '',
      otp: '',
      commonName: '',
      serialNumber: '',
      organizationIdentifier: '',
      organizationUnitName: '',
      organizationName: '',
      invoiceType: '1100',
      locationAddress: '',
      industryBusinessCategory: ''
    });
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleStepOne();
    } else if (activeStep === 1) {
      // إذا كانت البيانات موجودة بالفعل، ننتقل للمرحلة التالية مباشرة
      if (generatedData.complianceCertificate) {
        setActiveStep(2);
      } else {
        handleStepTwo();
      }
    } else if (activeStep === 2) {
      handleStepThree();
    } else if (activeStep === 3) {
      handleStepFour();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      active: { label: 'نشطة', color: 'success' as const, icon: <CheckCircleIcon /> },
      expired: { label: 'منتهية', color: 'error' as const, icon: <ErrorIcon /> },
      revoked: { label: 'ملغاة', color: 'error' as const, icon: <ErrorIcon /> },
      inactive: { label: 'غير نشطة', color: 'warning' as const, icon: <WarningIcon /> }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const getEnvironmentChip = (environment: string) => {
    return (
      <Chip
        label={environment === 'sandbox' ? 'تجريبي' : 'إنتاج'}
        color={environment === 'sandbox' ? 'info' : 'success'}
        size="small"
      />
    );
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA');
  };

  const getSuccessRate = (cert: ZatcaCertificate) => {
    if (!cert.usageStats || cert.usageStats.totalInvoices === 0) return 0;
    return Math.round(
      (cert.usageStats.successfulSubmissions / cert.usageStats.totalInvoices) * 100
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // حساب الشهادات المعروضة في الصفحة الحالية
  const paginatedCertificates = certificates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4">شهادات فاتورة (ZATCA)</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SecurityIcon />}
            onClick={() => setOpenGenerateDialog(true)}
          >
            توليد شهادة تلقائياً
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            رفع شهادة يدوياً
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>اسم الشهادة</TableCell>
                <TableCell>الفرع</TableCell>
                <TableCell>البيئة</TableCell>
                <TableCell>معرف الجهاز</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>تاريخ الانتهاء</TableCell>
                <TableCell>الإحصائيات</TableCell>
                <TableCell>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certificates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      لا توجد شهادات. قم بإضافة شهادة جديدة للبدء.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCertificates.map((cert) => (
                  <TableRow key={cert._id}>
                    <TableCell>{cert.name}</TableCell>
                    <TableCell>{cert.branchName || '-'}</TableCell>
                    <TableCell>{getEnvironmentChip(cert.environment)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {cert.deviceId}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(cert.status)}</TableCell>
                    <TableCell>{formatDate(cert.expiryDate)}</TableCell>
                    <TableCell>
                      {cert.usageStats && (
                        <Tooltip
                          title={`إجمالي: ${cert.usageStats.totalInvoices} | نجح: ${cert.usageStats.successfulSubmissions} | فشل: ${cert.usageStats.failedSubmissions}`}
                        >
                          <Chip
                            label={`${getSuccessRate(cert)}%`}
                            size="small"
                            color={getSuccessRate(cert) >= 90 ? 'success' : 'warning'}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* ✅ إخفاء زر التعديل للشهادات المولدة تلقائياً */}
                      {!cert.productionCertificate && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(cert)}
                          title="تعديل"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {/* ✅ زر عرض التفاصيل للشهادات المولدة تلقائياً */}
                      {cert.productionCertificate && (
                        <Tooltip title="عرض التفاصيل">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCertificate(cert);
                              setOpenViewDialog(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(cert._id)}
                        title="حذف"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={certificates.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
            }
          />
        </TableContainer>
      )}

      {/* Dialog لإضافة/تعديل الشهادة */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCertificate ? 'تعديل الشهادة' : 'إضافة شهادة جديدة'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="اسم الشهادة"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>الفرع (اختياري)</InputLabel>
              <Select
                value={formData.branchId}
                onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                label="الفرع (اختياري)"
              >
                <MenuItem value="">
                  <em>بدون فرع محدد</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>البيئة</InputLabel>
              <Select
                value={formData.environment}
                onChange={(e) =>
                  setFormData({ ...formData, environment: e.target.value as 'sandbox' | 'production' })
                }
                label="البيئة"
              >
                <MenuItem value="sandbox">تجريبي (Sandbox)</MenuItem>
                <MenuItem value="production">إنتاج (Production)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="معرف الجهاز (Device ID)"
              value={formData.deviceId}
              onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="رمز OTP (اختياري)"
              value={formData.otp}
              onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
              fullWidth
              helperText="رمز OTP للتسجيل (إذا كان مطلوباً)"
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                ملف الشهادة (.pem) {!selectedCertificate && '*'}
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                {formData.certificateFile ? formData.certificateFile.name : 'اختر ملف الشهادة'}
                <input
                  type="file"
                  hidden
                  accept=".pem,.crt,.cer"
                  onChange={handleFileChange('certificateFile')}
                />
              </Button>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                ملف المفتاح الخاص (.key) {!selectedCertificate && '*'}
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                {formData.privateKeyFile ? formData.privateKeyFile.name : 'اختر ملف المفتاح الخاص'}
                <input
                  type="file"
                  hidden
                  accept=".key,.pem"
                  onChange={handleFileChange('privateKeyFile')}
                />
              </Button>
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              loading ||
              !formData.name ||
              !formData.deviceId ||
              (!selectedCertificate && (!formData.certificateFile || !formData.privateKeyFile))
            }
          >
            {loading ? <CircularProgress size={24} /> : selectedCertificate ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog التوليد التلقائي - نظام المراحل الثلاث */}
      <Dialog
        open={openGenerateDialog}
        onClose={handleCloseGenerateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6">توليد شهادة رقمية - ZATCA</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Stepper للمراحل */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* المرحلة الأولى: توليد المفاتيح والـ CSR */}
            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  المعلومات الأساسية
                </Typography>

                <TextField
                  label="اسم الشهادة"
                  value={generateFormData.name}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, name: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: شهادة فرع الرياض - تجريبي"
                />

                <FormControl fullWidth>
                  <InputLabel>الفرع (اختياري)</InputLabel>
                  <Select
                    value={generateFormData.branchId}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, branchId: e.target.value })}
                    label="الفرع (اختياري)"
                  >
                    <MenuItem value="">
                      <em>بدون فرع محدد</em>
                    </MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>البيئة</InputLabel>
                  <Select
                    value={generateFormData.environment}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, environment: e.target.value as 'sandbox' | 'production' })}
                    label="البيئة"
                  >
                    <MenuItem value="sandbox">تجريبي (Sandbox)</MenuItem>
                    <MenuItem value="production">إنتاج (Production)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="معرف الجهاز (Device ID)"
                  value={generateFormData.deviceId}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, deviceId: e.target.value })}
                  required
                  fullWidth
                  helperText="معرف فريد للجهاز من بوابة فاتورة"
                />

                <TextField
                  label="رمز OTP"
                  value={generateFormData.otp}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, otp: e.target.value })}
                  required
                  fullWidth
                  helperText="رمز OTP من بوابة فاتورة (صالح لمدة ساعة واحدة)"
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  معلومات المنشأة
                </Typography>

                <TextField
                  label="1. Common Name"
                  value={generateFormData.commonName}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, commonName: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: TST-886431145-399999999900003"
                />

                <TextField
                  label="2. Serial Number"
                  value={generateFormData.serialNumber}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, serialNumber: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: 1-TST|2-TST|3-ed22f1d8-e6a2-1118-9b58-d9a8f11e445f"
                />

                <TextField
                  label="3. Organization Identifier (الرقم الضريبي)"
                  value={generateFormData.organizationIdentifier}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, organizationIdentifier: e.target.value })}
                  required
                  fullWidth
                  helperText="15 رقم - مثال: 399999999900003"
                  inputProps={{ maxLength: 15 }}
                />

                <TextField
                  label="4. Organization Unit Name (اسم الفرع)"
                  value={generateFormData.organizationUnitName}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, organizationUnitName: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: Riyadh Branch أو فرع الرياض"
                />

                <TextField
                  label="5. Organization Name (اسم الشركة)"
                  value={generateFormData.organizationName}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, organizationName: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: Maximum Speed Tech Supply LTD"
                />

                <FormControl fullWidth required>
                  <InputLabel>7. Invoice Type (نوع الفاتورة)</InputLabel>
                  <Select
                    value={generateFormData.invoiceType}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, invoiceType: e.target.value })}
                    label="7. Invoice Type (نوع الفاتورة)"
                  >
                    <MenuItem value="1100">1100 - مبسطة وقياسية</MenuItem>
                    <MenuItem value="0100">0100 - قياسية فقط</MenuItem>
                    <MenuItem value="1000">1000 - مبسطة فقط</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="8. Location Address (رمز العنوان الوطني)"
                  value={generateFormData.locationAddress}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, locationAddress: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: RRRD2929 (8 أحرف)"
                  inputProps={{ maxLength: 8 }}
                />

                <TextField
                  label="9. Industry Business Category (نوع النشاط)"
                  value={generateFormData.industryBusinessCategory}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, industryBusinessCategory: e.target.value })}
                  required
                  fullWidth
                  helperText="مثال: Supply activities أو أنشطة توريدات"
                />
              </Box>
            )}

            {/* المرحلة الثانية: نتائج اختبار الامتثال */}
            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {success && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    {success}
                  </Alert>
                )}

                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  نتائج مرحلة الامتثال
                </Typography>

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* معرف CSR */}
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                          معرف CSR:
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                          {generatedData.csrId}
                        </Typography>
                      </Box>

                      <Divider />

                      {/* معرف شهادة الامتثال */}
                      {generatedData.complianceCertificate?.requestId && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            معرف شهادة الامتثال (Compliance CSID):
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {generatedData.complianceCertificate.requestId}
                          </Typography>
                        </Box>
                      )}

                      <Divider />

                      {/* نتائج الاختبارات */}
                      {generatedData.complianceResult && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                            نتائج اختبارات الامتثال:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`نجح: ${generatedData.complianceResult.summary?.passed || 0}/${generatedData.complianceResult.summary?.total || 0}`}
                              color="success"
                              size="small"
                            />
                            {generatedData.complianceResult.summary?.failed > 0 && (
                              <Chip
                                icon={<ErrorIcon />}
                                label={`فشل: ${generatedData.complianceResult.summary.failed}`}
                                color="error"
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>
                      )}

                      <Divider />

                      {/* أزرار التحميل */}
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                          تحميل الملفات:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadFile(generatedData.csr!, `csr-${Date.now()}.pem`)}
                          >
                            تحميل CSR
                          </Button>
                          {generatedData.privateKey && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadFile(generatedData.privateKey!, `private-key-${Date.now()}.pem`)}
                            >
                              تحميل المفتاح الخاص
                            </Button>
                          )}
                          {generatedData.publicKey && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadFile(generatedData.publicKey!, `public-key-${Date.now()}.pem`)}
                            >
                              تحميل المفتاح العام
                            </Button>
                          )}
                          {generatedData.complianceCertificate && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DescriptionIcon />}
                              onClick={() => {
                                const blob = new Blob([JSON.stringify(generatedData.complianceCertificate, null, 2)], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `compliance-cert-${Date.now()}.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                            >
                              تحميل شهادة الامتثال
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                  ✅ تم اجتياز جميع اختبارات الامتثال بنجاح! يمكنك الآن الانتقال للمرحلة التالية لإرسال الطلب للهيئة.
                </Alert>
              </Box>
            )}

            {/* المرحلة الثالثة: استلام الشهادة */}
            {activeStep === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {success && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    {success}
                  </Alert>
                )}

                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  معلومات الشهادة النهائية
                </Typography>

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          معرف الشهادة:
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {generatedData.certificateId}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          اسم الشهادة:
                        </Typography>
                        <Typography variant="body1">
                          {generateFormData.name}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          البيئة:
                        </Typography>
                        <Chip
                          label={generateFormData.environment === 'sandbox' ? 'تجريبي' : 'إنتاج'}
                          color={generateFormData.environment === 'sandbox' ? 'info' : 'success'}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Alert severity="success" icon={<CheckCircleIcon />}>
                  ✅ تم استلام الشهادة من الهيئة بنجاح! اضغط على "إنهاء" لحفظ الشهادة في النظام.
                </Alert>
              </Box>
            )}

            {/* حالة التوليد */}
            {generating && (
              <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mt: 2 }}>
                {generationStep || 'جاري المعالجة...'}
              </Alert>
            )}

            {/* رسائل الخطأ */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerateDialog} disabled={generating}>
            إلغاء
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={generating}>
              السابق
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={generating || (activeStep === 3 && certificateSaved)}
            startIcon={generating ? <CircularProgress size={20} /> : activeStep === 3 ? <CheckCircleIcon /> : <SendIcon />}
          >
            {generating ? 'جاري المعالجة...' : activeStep === 3 ? (certificateSaved ? 'تم الحفظ ✓' : 'إنهاء') : 'التالي'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ نافذة عرض تفاصيل الشهادة المولدة تلقائياً */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">تفاصيل الشهادة</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCertificate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    معلومات أساسية
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">اسم الشهادة:</Typography>
                      <Typography variant="body2" fontWeight="bold">{selectedCertificate.name}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">الفرع:</Typography>
                      <Typography variant="body2">{selectedCertificate.branchName || 'غير محدد'}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">البيئة:</Typography>
                      <Chip
                        label={selectedCertificate.environment === 'sandbox' ? 'تجريبي' : 'إنتاج'}
                        color={selectedCertificate.environment === 'sandbox' ? 'info' : 'success'}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">معرف الجهاز:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selectedCertificate.deviceId}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">الحالة:</Typography>
                      {getStatusChip(selectedCertificate.status)}
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">تاريخ الانتهاء:</Typography>
                      <Typography variant="body2">{formatDate(selectedCertificate.expiryDate)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {selectedCertificate.productionCertificate && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      معلومات Production CSID
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {selectedCertificate.productionCertificate.requestId && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Request ID:</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {selectedCertificate.productionCertificate.requestId}
                          </Typography>
                        </Box>
                      )}
                      
                      {selectedCertificate.productionCertificate.tokenType && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">نوع Token:</Typography>
                          <Chip label={selectedCertificate.productionCertificate.tokenType} size="small" />
                        </Box>
                      )}
                      
                      <Alert severity="info" icon={<SecurityIcon />}>
                        هذه شهادة تم توليدها تلقائياً من هيئة الزكاة. لا يمكن تعديلها.
                      </Alert>
                      
                      {/* ✅ أزرار تحميل المفاتيح والشهادة */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          تحميل الملفات:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              window.open(
                                `/api/zatca/certificates/${selectedCertificate._id}/download?fileType=certificate`,
                                '_blank'
                              );
                            }}
                          >
                            الشهادة
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              window.open(
                                `/api/zatca/certificates/${selectedCertificate._id}/download?fileType=privateKey`,
                                '_blank'
                              );
                            }}
                          >
                            المفتاح الخاص
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              window.open(
                                `/api/zatca/certificates/${selectedCertificate._id}/download?fileType=publicKey`,
                                '_blank'
                              );
                            }}
                          >
                            المفتاح العام
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {selectedCertificate.usageStats && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      إحصائيات الاستخدام
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">إجمالي الفواتير:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedCertificate.usageStats.totalInvoices}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">الإرسالات الناجحة:</Typography>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {selectedCertificate.usageStats.successfulSubmissions}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">الإرسالات الفاشلة:</Typography>
                        <Typography variant="body2" color="error.main" fontWeight="bold">
                          {selectedCertificate.usageStats.failedSubmissions}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">معدل النجاح:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {getSuccessRate(selectedCertificate).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZatcaCertificateSettings;